<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useSideModals } from "~/composables/utils/useSideModals";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import { useCopilotPanelStore } from "~/store/copilot/copilot-panel.store";
import { useSessionDetailsAttendeesStore } from "~/store/sessions/session-details-attendees-store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { useEmployeeTrainingsStore } from "~/store/employee-trainings/employee-trainings.store";

/*
 * The Simbel AI trigger, pinned to the bottom-right corner of every page: the header no longer
 * carries one, so this is the only way into the panel outside the home banner.
 */
const attendanceReviewStore = useAttendanceReviewStore();
const copilotPanelStore = useCopilotPanelStore();
const createTrainingStore = useCreateTrainingStore();
const employeeTrainingsStore = useEmployeeTrainingsStore();
const sessionDetailsAttendeesStore = useSessionDetailsAttendeesStore();
const { hasOpenSideModal } = useSideModals();
const route = useRoute();

/* Split so each glyph can carry its own reveal delay; `Array.from` keeps the accented « à » whole. */
const HOVER_LABEL = "Demander à l'IA";
const labelLetters = Array.from(HOVER_LABEL);

/*
 * Five cases stand it down:
 * - a public page (`isLoggedIn: false`), such as the partner's attendee-list link or a survey — even for a
 *   logged-in admin, the assistant does not belong there;
 * - the panel is open — it takes the very corner the button rests in, and closes from its own cross;
 * - a `SideModal` is on screen. It is anchored to the same right edge and the button lands on its
 *   footer actions. A z-index cannot settle this: a side modal opened *inside* a session detail is
 *   trapped in that `GlobalModal`'s stacking context (500), so no value it carries can beat a button
 *   painted outside it — and lowering the button under 500 would lose the session detail, which is
 *   exactly where the assistant has to stay reachable;
 * - the « Ajout de participant(s) » modal is on screen, for the same reason: it fills the session detail
 *   and its footer puts the confirm button, with the tooltip saying why it is disabled, in this corner;
 * - the attendance review or the training form's exit confirm is on screen. `CopilotShell` drops
 *   *under* those (`above-modals`), so a press here would open the panel behind them: a dead button.
 *   Every other modal, a session detail and the training form included, keeps it.
 */
const isStoodDown = computed(
  () =>
    route.meta.isLoggedIn === false ||
    copilotPanelStore.isOpen ||
    hasOpenSideModal.value ||
    sessionDetailsAttendeesStore.showAddAttendeeModal ||
    attendanceReviewStore.isOpen ||
    createTrainingStore.showExit,
);

/*
 * Below `sm`, a participant's session detail pins its actions, stacked full width, to the bottom edge. How many
 * apply varies, so no fixed lift clears them: the button steps aside there, through the bar's own breakpoint.
 * The hosts render that detail on this same condition.
 */
const isOverSessionActionBar = computed(() => employeeTrainingsStore.showMySessionModal && !!route.query.id?.length);
</script>

<template>
  <!--
    Over the whole 500 modal layer — a session detail is a full-screen `GlobalModal` and the
    assistant has to stay one press away from there too; still under the panel itself (600) and the
    header overlays (700).

    The corner clears whatever owns the bottom of the screen, through the very breakpoint that puts
    it there rather than through a measured viewport, so nothing shifts after the first paint:
    - above 768px, an `IndexLayout`'s pagination. It is in the page flow and ends ~63px off the
      bottom edge (`page_container` `pb-3` 12 + the row's ~39 + `Pagination`'s `pt-3` 12), and its
      right half — « éléments par page » — is in this very corner, so 64px is the floor: lower, and
      the button lands on it;
    - below it, `NavBarMobile` — 70px tall, `fixed bottom-0`, and it covers that same pagination.

    The surface is the panel's own — white, bordered, blue `ai` glyph — rather than a solid accent
    disc: it is the same object. The page ground it floats over is white, so the border takes the
    darker `stroke-grey-color` and the lift comes from `--shadow-floating`, two layers cast downwards
    rather than the flat glow the panel uses.

    No fixed width: `px-3` around the 32px glyph *is* the 56px disc, so the bubble is free to
    grow to whatever the label needs. It grows leftwards on its own — the corner is pinned by
    `right-6` — and the glyph leads, so it travels left ahead of the letters typing in behind it.
  -->
  <button
    v-if="!isStoodDown"
    type="button"
    class="group fixed bottom-[86px] right-6 z-[550] flex h-14 cursor-pointer items-center justify-center rounded-full border border-stroke-grey-color bg-light-color px-3 shadow-[shadow:var(--shadow-floating)] transition-colors hover:bg-lightest-grey-color min-[769px]:bottom-[64px]"
    :class="{ 'max-sm:hidden': isOverSessionActionBar }"
    data-testid="copilot-floating-trigger"
    aria-label="Ouvrir Simbel AI"
    @click="copilotPanelStore.toggle()"
  >
    <SvgIcon name="ai" class="pointer-events-none h-8 w-8 shrink-0 text-primary-blue-color" />
    <!-- Decorative: the accessible name stays on the button, so the letters must not be announced. -->
    <span
      class="small_demi ms-0 inline-flex overflow-hidden whitespace-nowrap text-primary-color transition-[margin-inline-start] duration-[120ms] ease-[ease] group-hover:ms-2 group-focus-visible:ms-2"
      :style="{ '--letters': labelLetters.length }"
      aria-hidden="true"
    >
      <!--
        `max-width` rather than `width`: no glyph reaches 1.5em, so the cap opens each letter to its
        own size without measuring one. `min-w-0` is what lets it close back to 0 — flex items floor
        at their min-content width otherwise — and `whitespace-pre` keeps the label's two spaces from
        collapsing there. Only the stagger is opt-in: under a reduced-motion preference the delays are
        simply never declared, so the label still opens, in one 120ms move rather than fifteen. The
        closing one runs the other way round, last letter first.
      -->
      <span
        v-for="(letter, index) in labelLetters"
        :key="`${letter}-${index}`"
        class="inline-block min-w-0 max-w-0 whitespace-pre opacity-0 transition-[max-width,opacity] duration-[120ms] ease-[ease] group-hover:max-w-[1.5em] group-hover:opacity-100 group-focus-visible:max-w-[1.5em] group-focus-visible:opacity-100 motion-safe:[transition-delay:calc((var(--letters)_-_var(--index)_-_1)_*_22ms)] motion-safe:group-hover:[transition-delay:calc(var(--index)_*_22ms)] motion-safe:group-focus-visible:[transition-delay:calc(var(--index)_*_22ms)]"
        :style="{ '--index': index }"
        >{{ letter }}</span
      >
    </span>
  </button>
</template>
